import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const PRESTADORES_MAP: Record<number, number> = {
  0: 3,
  1: 10,
  2: 30,
  3: 80,
  4: 100,
};

const TOTAL_STEPS = 4;

export default function OnboardingWizard() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { track } = useAnalytics();

  useEffect(() => {
    track('onboarding_step_viewed', { step });
  }, [step, track]);

  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [sectorIndex, setSectorIndex] = useState<number | null>(null);
  const [setorOutro, setSetorOutro] = useState('');
  const [countryIndex, setCountryIndex] = useState<number | null>(null);
  const [providerRangeIndex, setProviderRangeIndex] = useState<number | null>(null);

  const sectors = t('onboarding.sectors', { returnObjects: true }) as string[];
  const countries = t('onboarding.countries', { returnObjects: true }) as string[];
  const providerRanges = t('onboarding.providerRanges', { returnObjects: true }) as string[];

  const selectedSector = sectorIndex !== null ? sectors[sectorIndex] : '';
  const isOtherSector = sectorIndex !== null && sectorIndex === sectors.length - 1;
  const resolvedSetor = isOtherSector ? setorOutro : selectedSector;

  const selectedCountry = countryIndex !== null ? countries[countryIndex] : '';
  const selectedProviderRange =
    providerRangeIndex !== null ? providerRanges[providerRangeIndex] : '';

  function canAdvance(): boolean {
    switch (step) {
      case 1:
        return nomeEmpresa.trim().length >= 2;
      case 2:
        return sectorIndex !== null && (!isOtherSector || setorOutro.trim().length >= 2);
      case 3:
        return countryIndex !== null;
      case 4:
        return providerRangeIndex !== null;
      default:
        return false;
    }
  }

  async function handleSubmit() {
    if (providerRangeIndex === null) return;
    setLoading(true);
    try {
      const { error } = await supabase.rpc('criar_organizacao', {
        p_nome: nomeEmpresa.trim(),
        p_setor: resolvedSetor,
        p_pais: selectedCountry,
        p_num_prestadores: PRESTADORES_MAP[providerRangeIndex],
      });

      if (error) {
        toast.error(error.message || t('onboarding.createError'));
        setLoading(false);
        return;
      }

      track('onboarding_completed');
      toast.success(t('onboarding.companyCreated'));
      window.location.href = '/painel';
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('onboarding.createError');
      toast.error(message);
      setLoading(false);
    }
  }

  function renderStepContent() {
    switch (step) {
      case 1:
        return (
          <div className="space-y-3">
            <Label htmlFor="nome-empresa">{t('onboarding.companyName')}</Label>
            <Input
              id="nome-empresa"
              placeholder={t('onboarding.companyNamePlaceholder')}
              value={nomeEmpresa}
              onChange={(e) => setNomeEmpresa(e.target.value)}
              autoFocus
              className="h-12 rounded-2xl"
            />
            {nomeEmpresa.length > 0 && nomeEmpresa.trim().length < 2 && (
              <p className="text-xs text-destructive">{t('onboarding.companyNameMinLength')}</p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-3">
            <Label>{t('onboarding.sector')}</Label>
            <Select
              value={sectorIndex !== null ? String(sectorIndex) : ''}
              onValueChange={(v) => setSectorIndex(Number(v))}
            >
              <SelectTrigger className="h-12 rounded-2xl">
                <SelectValue placeholder={t('onboarding.selectSector')} />
              </SelectTrigger>
              <SelectContent>
                {sectors.map((s, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOtherSector && (
              <div className="space-y-2">
                <Label htmlFor="setor-outro">{t('onboarding.otherSector')}</Label>
                <Input
                  id="setor-outro"
                  placeholder={t('onboarding.otherSectorPlaceholder')}
                  value={setorOutro}
                  onChange={(e) => setSetorOutro(e.target.value)}
                  autoFocus
                  className="h-12 rounded-2xl"
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-3">
            <Label>{t('onboarding.country')}</Label>
            <Select
              value={countryIndex !== null ? String(countryIndex) : ''}
              onValueChange={(v) => setCountryIndex(Number(v))}
            >
              <SelectTrigger className="h-12 rounded-2xl">
                <SelectValue placeholder={t('onboarding.selectCountry')} />
              </SelectTrigger>
              <SelectContent>
                {countries.map((p, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 4:
        return (
          <div className="space-y-3">
            <Label>{t('onboarding.teamSize')}</Label>
            <Select
              value={providerRangeIndex !== null ? String(providerRangeIndex) : ''}
              onValueChange={(v) => setProviderRangeIndex(Number(v))}
            >
              <SelectTrigger className="h-12 rounded-2xl">
                <SelectValue placeholder={t('onboarding.selectRange')} />
              </SelectTrigger>
              <SelectContent>
                {providerRanges.map((opt, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {providerRangeIndex !== null && (
              <div className="ow-card mt-6">
                <div className="p-4 space-y-2">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                    {t('onboarding.summary')}
                  </p>
                  <dl className="flex flex-col gap-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">
                        {t('onboarding.companyLabel')}
                      </dt>
                      <dd className="font-medium">{nomeEmpresa.trim()}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">
                        {t('onboarding.sectorLabel')}
                      </dt>
                      <dd className="font-medium">{resolvedSetor}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">
                        {t('onboarding.countryLabel')}
                      </dt>
                      <dd className="font-medium">{selectedCountry}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">
                        {t('onboarding.providersLabel')}
                      </dt>
                      <dd className="font-medium">{selectedProviderRange}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  }

  // Progress dots (steps)
  const dots = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);

  return (
    <div className="w-full max-w-md ow-card">
      <div className="p-6 flex flex-col gap-5">
        <div className="text-center">
          <h2 className="text-xl font-bold tracking-tight">{t('onboarding.title')}</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {t('onboarding.stepOf', { step, total: TOTAL_STEPS })}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5">
          {dots.map((d) => (
            <span
              key={d}
              className="rounded-full transition-all"
              style={{
                width: d === step ? 24 : 8,
                height: 8,
                background:
                  d <= step ? 'hsl(var(--foreground))' : 'hsl(var(--muted))',
              }}
              aria-hidden
            />
          ))}
        </div>

        {renderStepContent()}

        <div className="flex justify-between gap-3 pt-2">
          <button
            type="button"
            className="ow-btn ow-btn--outline ow-btn--sm"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 1 || loading}
            style={step === 1 ? { visibility: 'hidden' } : undefined}
          >
            <ArrowLeft size={14} />
            {t('common.back')}
          </button>

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              className="ow-btn ow-btn--primary ow-btn--sm"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance()}
            >
              {t('common.next')}
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              className="ow-btn ow-btn--primary ow-btn--sm"
              onClick={handleSubmit}
              disabled={!canAdvance() || loading}
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {t('onboarding.createCompany')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
